import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import axios from "axios";
import cn from "classnames";
import { CSSTransition } from "react-transition-group";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import ImageLoader from "@shared/ImageLoader";
import EditImageBusinessGifts from "@shared/Modals/EditImageBusinessGifts";
import { createFaceCenteredCrop, warmupFaceDetection } from "../../../utils/faceDetection";
import {
  businessGiftCampaignState,
  businessGiftFormState,
  businessGiftImagesState,
  businessGiftFrameState,
  businessGiftLoadingState,
  businessGiftSubmissionResultState,
  businessGiftErrorState,
  businessGiftEditModalState,
} from "../../Recoil/Atoms/businessGifts";
import "./style.css";
import "../../Shared/WindowModals/style.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Frame data
const Frames = [
  { name: "classic", img: "assets/file/images/d1.png", text: "קלאסי", frameBg: "/assets/file/images/frame_classic.png" },
  { name: "bold", img: "assets/file/images/d2.png", text: "נועז", label: "פופולארי", frameBg: "/assets/file/images/frame_bold.png" },
  { name: "ever", img: "assets/file/images/d3.png", text: "נקי", frameBg: "/assets/file/images/frame_even.png" },
  { name: "clean", img: "assets/file/images/d4.png", text: "אותנטי", frameBg: "/assets/file/images/frame_clean.png" },
  { name: "village", img: "assets/file/images/d5.png", text: "כפרי", label: "חדש", frameBg: "/assets/file/images/frame_village.png" },
  { name: "nordic", img: "assets/file/images/d6.png", text: "נורדי", label: "חדש", frameBg: "/assets/file/images/frame_nordic.png" },
];

// Get frame background URL
const getFrameBackground = (frameName) => {
  const frame = Frames.find(f => f.name === frameName);
  return frame ? frame.frameBg : Frames[0].frameBg;
};

const BusinessGifts = () => {
  const { businessCode } = useParams();
  const { width } = useWindowDimensions();

  // Recoil state
  const [campaign, setCampaign] = useRecoilState(businessGiftCampaignState);
  const [formData, setFormData] = useRecoilState(businessGiftFormState);
  const [images, setImages] = useRecoilState(businessGiftImagesState);
  const [selectedFrame, setSelectedFrame] = useRecoilState(businessGiftFrameState);
  const [loading, setLoading] = useRecoilState(businessGiftLoadingState);
  const [submissionResult, setSubmissionResult] = useRecoilState(businessGiftSubmissionResultState);
  const [error, setError] = useRecoilState(businessGiftErrorState);
  const [editModal, setEditModal] = useRecoilState(businessGiftEditModalState);

  // Local state
  const [showSuccess, setShowSuccess] = useState(false);
  const [mobileOptionsImage, setMobileOptionsImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);

  // LocalStorage key for this business
  const storageKey = `businessGift_${businessCode}`;

  // Load saved data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(storageKey);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.images?.length > 0) {
          setImages(parsed.images);
        }
        if (parsed.formData) {
          setFormData(parsed.formData);
        }
        if (parsed.selectedFrame) {
          setSelectedFrame(parsed.selectedFrame);
        }
      } catch (e) {
        console.error("Error loading saved data:", e);
      }
    }
  }, [businessCode]);

  // Save data to localStorage when it changes
  useEffect(() => {
    // Filter out uploading images (they have blob URLs that won't persist)
    const completedImages = images.filter(img => !img.isUploading);

    if (completedImages.length > 0 || formData.employeeName || formData.employeeEmail) {
      const dataToSave = {
        images: completedImages,
        formData,
        selectedFrame,
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } else {
      // Clear localStorage when there's no data
      localStorage.removeItem(storageKey);
    }
  }, [images, formData, selectedFrame, storageKey]);

  // Fetch campaign on mount
  useEffect(() => {
    fetchCampaign();
  }, [businessCode]);

  // Pre-warm face detection models when campaign is loaded
  useEffect(() => {
    if (campaign) {
      warmupFaceDetection().catch(err => {
        console.warn('[BusinessGifts] Face detection warmup failed:', err.message);
      });
    }
  }, [campaign]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (editModal.visible || mobileOptionsImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [editModal.visible, mobileOptionsImage]);

  // Handle image click - show options on mobile, edit directly on desktop
  const handleImageClick = (image) => {
    if (width <= 767) {
      setMobileOptionsImage(image);
    } else {
      handleEditImage(image);
    }
  };

  const handleMobileEdit = () => {
    if (mobileOptionsImage) {
      handleEditImage(mobileOptionsImage);
      setMobileOptionsImage(null);
    }
  };

  const handleMobileDelete = () => {
    if (mobileOptionsImage) {
      handleRemoveImage(mobileOptionsImage.imageId);
      setMobileOptionsImage(null);
    }
  };

  const fetchCampaign = async () => {
    setLoading(prev => ({ ...prev, fetchingCampaign: true }));
    setError(null);

    try {
      const response = await axios.get(`${BASE_URL}/business-gifts/${businessCode}`);
      if (response.data.success) {
        setCampaign(response.data.campaign);
        // Set default frame to first available
        if (response.data.campaign.availableFrameTypes?.length > 0) {
          setSelectedFrame(response.data.campaign.availableFrameTypes[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching campaign:", err);
      if (err.response?.status === 404) {
        setError("הקמפיין לא נמצא");
      } else if (err.response?.status === 403) {
        setError(err.response.data.message || "הקמפיין אינו פעיל");
      } else {
        setError("שגיאה בטעינת הקמפיין");
      }
    } finally {
      setLoading(prev => ({ ...prev, fetchingCampaign: false }));
    }
  };

  // Verify employee email against whitelist
  const handleVerifyEmail = async () => {
    if (!formData.employeeEmail?.trim()) {
      setEmailError("יש להזין כתובת אימייל");
      return;
    }

    setVerifyingEmail(true);
    setEmailError(null);

    try {
      const response = await axios.post(`${BASE_URL}/business-gifts/${businessCode}/validate-email`, {
        email: formData.employeeEmail.trim()
      });

      if (response.data.success && response.data.valid) {
        setEmailVerified(true);
        setEmailError(null);
      } else {
        setEmailError("כתובת האימייל לא נמצאה ברשימת העובדים המורשים");
        setEmailVerified(false);
      }
    } catch (err) {
      console.error("Error verifying email:", err);
      setEmailError("שגיאה באימות האימייל. אנא נסו שנית");
      setEmailVerified(false);
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check limit
    const remaining = campaign.maxImagesPerEmployee - images.length;
    if (files.length > remaining) {
      alert(`ניתן להעלות עד ${remaining} תמונות נוספות`);
      return;
    }

    // Process each file with client-side face detection and create temp images
    const tempImages = await Promise.all(files.map(async (file) => {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Try client-side face detection first (same as main app)
      let blobUrl;
      let clientCropData = null;
      let imageWidth = 0;
      let imageHeight = 0;

      try {
        const cropResult = await createFaceCenteredCrop(file);
        blobUrl = cropResult.url; // Use face-centered crop URL for preview
        clientCropData = cropResult.cropData;
        imageWidth = cropResult.cropData?.originalWidth || 0;
        imageHeight = cropResult.cropData?.originalHeight || 0;

        console.log(`[BusinessGifts] Face detection result:`, {
          faceDetected: cropResult.cropData?.faceDetected,
          faceCount: cropResult.cropData?.faceCount,
          cropPosition: { x: cropResult.cropData?.x, y: cropResult.cropData?.y },
          zoomFactor: cropResult.cropData?.zoomFactor,
        });
      } catch (faceError) {
        // Fallback to original blob if face detection fails
        console.warn('[BusinessGifts] Face detection failed, using original:', faceError.message);
        blobUrl = URL.createObjectURL(file);

        // Get dimensions from image
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = () => {
            imageWidth = img.width;
            imageHeight = img.height;
            resolve();
          };
          img.src = blobUrl;
        });
      }

      return {
        imageId: tempId,
        blobUrl: blobUrl,
        imageWidth: imageWidth,
        imageHeight: imageHeight,
        isUploading: true,
        file: file,
        clientCropData: clientCropData, // Store for upload
      };
    }));

    // Add all temp images immediately so frames appear
    setImages(prev => [...prev, ...tempImages]);

    // Upload each image in parallel
    tempImages.forEach(async (tempImage) => {
      try {
        const formdata = new FormData();
        formdata.append("image", tempImage.file);
        formdata.append("imagewidth", tempImage.imageWidth);
        formdata.append("imageheight", tempImage.imageHeight);

        // Send client-side crop coordinates to server
        // Server will use these instead of running its own detection
        if (tempImage.clientCropData) {
          const contentBounds = tempImage.clientCropData.contentBounds;
          formdata.append("clientCropData", JSON.stringify({
            left: tempImage.clientCropData.x,
            top: tempImage.clientCropData.y,
            width: tempImage.clientCropData.size,
            height: tempImage.clientCropData.size,
            faceDetected: tempImage.clientCropData.faceDetected,
            faceCount: tempImage.clientCropData.faceCount || 0,
            zoomFactor: tempImage.clientCropData.zoomFactor || 1.0,
            contentBounds: contentBounds || null,
            borderTypes: contentBounds?.borderTypes || null, // Include border types (black/white/mixed)
            viewport: tempImage.clientCropData.viewport || null, // Include viewport for accurate zoom positioning
            source: 'client-face-detection'
          }));
          console.log(`[BusinessGifts] Sending client crop data to server:`, {
            left: tempImage.clientCropData.x,
            top: tempImage.clientCropData.y,
            size: tempImage.clientCropData.size,
            zoomFactor: tempImage.clientCropData.zoomFactor || 1.0,
            viewport: tempImage.clientCropData.viewport || null,
            borderTypes: contentBounds?.borderTypes || null,
            hasBars: contentBounds?.hasBars || false
          });
        }

        const response = await axios.post(
          `${BASE_URL}/business-gifts/${businessCode}/upload-image`,
          formdata,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (response.data.success) {
          // Replace temp image with server response, keep blob URL for loading state
          setImages(prev => prev.map(img => {
            if (img.imageId === tempImage.imageId) {
              return {
                ...response.data.image,
                isUploading: false,
                blobUrl: tempImage.blobUrl, // Keep blob URL until final image loads
              };
            }
            return img;
          }));
        } else {
          // Remove failed upload
          setImages(prev => prev.filter(img => img.imageId !== tempImage.imageId));
          URL.revokeObjectURL(tempImage.blobUrl);
        }
      } catch (err) {
        console.error("Error uploading image:", err);
        // Remove failed upload
        setImages(prev => prev.filter(img => img.imageId !== tempImage.imageId));
        URL.revokeObjectURL(tempImage.blobUrl);
      }
    });

    e.target.value = ""; // Reset file input
  };

  const handleRemoveImage = (imageId) => {
    setImages(prev => prev.filter(img => img.imageId !== imageId));
  };

  const handleEditImage = (image) => {
    // Open the business gifts edit modal
    setEditModal({
      visible: true,
      image: image,
      businessCode: businessCode,
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({
      visible: false,
      image: null,
      businessCode: '',
    });
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.employeeName.trim()) {
      alert("נא להזין שם מלא");
      return;
    }

    if (images.length === 0) {
      alert("נא להעלות לפחות תמונה אחת");
      return;
    }

    // Validate email if required
    if (campaign.requireEmailValidation && !formData.employeeEmail.trim()) {
      alert("נא להזין כתובת אימייל");
      return;
    }

    setLoading(prev => ({ ...prev, submitting: true }));

    try {
      const response = await axios.post(`${BASE_URL}/business-gifts/${businessCode}/submit`, {
        employeeFullName: formData.employeeName.trim(),
        employeeEmail: formData.employeeEmail.trim() || null,
        frameType: selectedFrame,
        images: images.map(img => ({
          imageId: img.imageId,
          imageUrl: img.imageUrl,
          cropImageUrl: img.cropImageUrl,
          viewImageUrl: img.viewImageUrl,
          cropData: img.cropData || {},
          bwFilter: img.bwFilter || false,
          bluewhiteFilter: img.bluewhiteFilter || false,
        })),
      });

      if (response.data.success) {
        // Clear localStorage after successful submission
        localStorage.removeItem(storageKey);

        setSubmissionResult({
          success: true,
          submissionNumber: response.data.submissionNumber,
          error: null,
        });
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Error submitting:", err);
      setSubmissionResult({
        success: false,
        submissionNumber: null,
        error: err.response?.data?.message || "שגיאה בשליחת הטופס",
      });
      alert(err.response?.data?.message || "שגיאה בשליחת הטופס");
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  };

  // Filter frames based on campaign availability
  const availableFrames = Frames.filter(
    frame => campaign?.availableFrameTypes?.includes(frame.name)
  );

  // Loading state
  if (loading.fetchingCampaign) {
    return (
      <div className="business-gifts-page">
        <div className="business-gifts-loading">
          <div className="spinner"></div>
          <p>טוען...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="business-gifts-page">
        <div className="business-gifts-error">
          <h2>{error}</h2>
          <p>נא לוודא שהקישור תקין או ליצור קשר עם המעסיק</p>
        </div>
      </div>
    );
  }

  // Success state
  if (showSuccess) {
    return (
      <div className="business-gifts-page">
        <div className="business-gifts-success">
          <img className="success-icon" src="/assets/file/images/success_tick.png" width="92" height="85" alt="" aria-hidden="true" />
          <h2>ההגשה התקבלה בהצלחה!</h2>
          <p>מספר הגשה: {submissionResult.submissionNumber}</p>
          <p>תודה {formData.employeeName}!</p>
          <p>התמונות שלך יודפסו בקרוב</p>
        </div>
      </div>
    );
  }

  // Campaign not loaded yet
  if (!campaign) {
    return null;
  }

  return (
    <div className="business-gifts-page" dir="rtl">
      {/* Header with logos */}
      <div className="business-gifts-header">
        <div className="header-logos">
          <img src={campaign.logoUrl} alt={campaign.businessName} className="business-logo" />
          <span className="logo-separator">x</span>
          <img src="/assets/images/stk_logo.svg" alt="Blends" className="blends-logo" />
        </div>
      </div>

      {/* Main content */}
      <div className="business-gifts-content">
        {/* HR Message - shown above all fields */}
        {campaign.emailValidationMessage && (
          <div className="email-validation-message">
            <span className="gradient-text">{campaign.emailValidationMessage}</span>
          </div>
        )}

        {/* Employee form */}
        <div className="employee-form-section">
          <h2>פרטי העובד</h2>
          <div className="form-group">
            <label>שם מלא *</label>
            <input
              type="text"
              value={formData.employeeName}
              onChange={(e) => setFormData(prev => ({ ...prev, employeeName: e.target.value }))}
              placeholder="הזינו את שמכם המלא"
              className="form-input"
            />
          </div>
          {campaign.requireEmailValidation && (
            <>
              <div className="form-group">
                <label>אימייל *</label>
                <div className="email-input-row">
                  <input
                    type="email"
                    value={formData.employeeEmail}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, employeeEmail: e.target.value }));
                      // Reset verification when email changes
                      if (emailVerified) {
                        setEmailVerified(false);
                      }
                      setEmailError(null);
                    }}
                    placeholder="הזינו את כתובת האימייל"
                    className="form-input"
                    disabled={emailVerified}
                  />
                  {!emailVerified ? (
                    <button
                      type="button"
                      className="verify-email-button"
                      onClick={handleVerifyEmail}
                      disabled={verifyingEmail || !formData.employeeEmail?.trim()}
                    >
                      {verifyingEmail ? "מאמת..." : "אמת"}
                    </button>
                  ) : (
                    <span className="email-verified-badge">
                      <img src="/assets/file/images/success_icon.svg" width="16" height="16" alt="" aria-hidden="true" />
                      מאומת
                    </span>
                  )}
                </div>
                {emailError && (
                  <p className="email-error-message">{emailError}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Show upload and frame sections only if email validation not required OR email is verified */}
        {(!campaign.requireEmailValidation || emailVerified) && (
          <>
        {/* Image upload section */}
        <div className="image-upload-section">
          <h2>העלו תמונות ({images.length}/{campaign.maxImagesPerEmployee})</h2>

          {/* Upload button */}
          {images.length < campaign.maxImagesPerEmployee && (
            <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                id="image-upload"
                className="file-input"
                disabled={loading.uploadingImage}
              />
              <label htmlFor="image-upload" className="upload-button">
                {loading.uploadingImage ? (
                  <span>מעלה...</span>
                ) : (
                  <>
                    <span className="upload-icon">+</span>
                    <span>העלו תמונות</span>
                  </>
                )}
              </label>
            </div>
          )}

          {/* Image gallery - horizontal scrollable */}
          {images.length > 0 && (
            <div className="images-gallery-business">
              {images.map((image, index) => (
                <div key={`${image.imageId}-${selectedFrame}`} className="album-item-wrapper-business">
                  <div
                    className={cn(`album-item-business ${selectedFrame}`, { uploading: image.isUploading })}
                    style={{
                      background: `url(${getFrameBackground(selectedFrame)}) no-repeat`,
                      backgroundSize: 'contain'
                    }}
                    onClick={() => !image.isUploading && loadedImages[image.imageId] && handleImageClick(image)}
                  >
                    {/* Edit/Delete overlay - only show when fully loaded */}
                    {!image.isUploading && loadedImages[image.imageId] && (
                      <div className={`content-overlay-business ${selectedFrame}`}>
                        <div className="content-details-business">
                          <img
                            src="/assets/file/images/edit_image.svg"
                            alt="עריכה"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditImage(image);
                            }}
                          />
                          <img
                            src="/assets/file/images/Delete_icon.svg"
                            alt="מחיקה"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(image.imageId);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Show blob with spinner while uploading or loading final image, then show final image */}
                  {(image.isUploading || !loadedImages[image.imageId]) && image.blobUrl ? (
                    <>
                      <img
                        src={image.blobUrl}
                        className={cn(`img-con-business ${selectedFrame}`)}
                        alt="loading"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className={`upload-spinner-overlay ${selectedFrame}`}>
                        <div className="upload-spinner"></div>
                      </div>
                    </>
                  ) : null}
                  {/* Final image - hidden until loaded */}
                  {!image.isUploading && (
                    <ImageLoader
                      src={image.cropImageUrl || image.viewImageUrl}
                      className={cn(`img-con-business ${selectedFrame}`, { 'image-hidden': !loadedImages[image.imageId] })}
                      bwFilter={image.bwFilter}
                      bluewhiteFilter={image.bluewhiteFilter}
                      onLoad={() => {
                        // Revoke blob URL when final image loads
                        if (image.blobUrl) {
                          URL.revokeObjectURL(image.blobUrl);
                        }
                        setLoadedImages(prev => ({ ...prev, [image.imageId]: true }));
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Frame selector - moved below images */}
        <div className="frame-selector-section">
          <h2>בחרו מסגרת</h2>
          <div className="frame-wrapper-business">
            {availableFrames.map((frame) => (
              <div
                key={frame.name}
                className={cn("frame-item", { selected: selectedFrame === frame.name })}
                onClick={() => setSelectedFrame(frame.name)}
              >
                {frame.label && <span className="frame-label">{frame.label}</span>}
                <img src={`/${frame.img}`} alt={frame.text} />
                <p>{frame.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Submit button */}
        <div className="submit-section">
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={loading.submitting || images.length === 0 || !formData.employeeName.trim()}
          >
            {loading.submitting ? "שולח..." : "שלחו את התמונות"}
          </button>
        </div>
          </>
        )}
      </div>

      {/* Mobile Options Prompt - rendered via Portal to ensure fixed positioning works */}
      {mobileOptionsImage && ReactDOM.createPortal(
        <div
          className="mobile-options-overlay"
          onClick={() => setMobileOptionsImage(null)}
        >
          <div className="mobile-options-prompt" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-option-btn edit-btn" onClick={handleMobileEdit}>
              <span>עריכה</span>
              <img src="/assets/file/images/edit_image.svg" alt="עריכה" />
            </button>
            <button className="mobile-option-btn delete-btn" onClick={handleMobileDelete}>
              <span>מחיקת תמונה</span>
              <img src="/assets/file/images/Delete_icon.svg" alt="מחיקה" />
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Image Modal - rendered via Portal to ensure fixed positioning works */}
      {ReactDOM.createPortal(
        <div
          className={cn("modal-overlay business-gifts-modal-overlay", {
            show: editModal.visible,
            hide: !editModal.visible,
          })}
          onClick={(e) => {
            if (e.target.className.includes("modal-overlay") && e.target.className.includes("show")) {
              handleCloseEditModal();
            }
          }}
          style={{ zIndex: 1000 }}
        >
          <CSSTransition
            in={editModal.visible}
            timeout={300}
            classNames="css-transition"
            unmountOnExit
          >
            <EditImageBusinessGifts
              handleCloseModal={handleCloseEditModal}
              modalData={{
                image: editModal.image,
                businessCode: editModal.businessCode,
                frame: selectedFrame,
              }}
            />
          </CSSTransition>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BusinessGifts;
